import { Circle as CircleIcon, Flag as FlagIcon, FlagOutlined as FlagOutlinedIcon } from "@mui/icons-material";
import {
  Badge,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { DisplayMode } from "../../data/BaseEntity";
import type { DisplayModeCounts } from "../useFilteredOrders";

type FilterableDisplayMode = keyof DisplayModeCounts;

interface DisplayModeOption {
  readonly value: FilterableDisplayMode;
  readonly label: string;
  readonly color: "error" | "warning";
}

const OPTIONS: readonly DisplayModeOption[] = [
  { value: DisplayMode.OVERDUE, label: "Po terminie", color: "error" },
  { value: DisplayMode.WARNING, label: "Zbliża się", color: "warning" },
];

interface Props {
  readonly values: DisplayMode[];
  readonly counts: DisplayModeCounts;
  readonly onChange: (values: DisplayMode[]) => void;
}

/** Icon button opening a multi-select menu of order card colors, each with the number of matching orders. */
const DisplayModeFilter: FC<Props> = ({ values, counts, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const toggle = (value: DisplayMode) =>
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
  const activeColor = OPTIONS.find((option) => values.includes(option.value))?.color ?? "inherit";

  return (
    <>
      <Tooltip title="Termin">
        <IconButton aria-label="Termin" color={activeColor} onClick={(e) => setAnchor(e.currentTarget)}>
          <Badge badgeContent={values.length} color="primary" invisible={values.length === 0}>
            {values.length > 0 ? <FlagIcon /> : <FlagOutlinedIcon />}
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            role="menuitemcheckbox"
            aria-checked={values.includes(option.value)}
            onClick={() => toggle(option.value)}
          >
            <Checkbox edge="start" checked={values.includes(option.value)} tabIndex={-1} disableRipple />
            <ListItemIcon>
              <CircleIcon fontSize="small" color={option.color} />
            </ListItemIcon>
            <ListItemText primary={option.label} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 3 }}>
              {counts[option.value]}
            </Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default DisplayModeFilter;
